import { DATE_INPUT_FORMAT, TODAY, TODAY_EOD } from '@/constants';
import { Datetime, saveFile } from '@/utils';
import { useCallback, useEffect, useState } from 'react';

import AirlineIataDropdown from '@/components/AirlineDropdown';
import AirportDropdown from '@/components/AirportDropdown';
import { Button } from 'react-bootstrap';
import DateRangePicker from '@/components/DateRangePicker';
import { DispatchIssueWithCacheTaxiUsedEnum } from '@/models/gen/graphql';
import EnumDropdown from '@/components/EnumDropdown';
import FormButton from '@/components/FormButton';
import { InputWithIcon } from '@/components/Input';
import IssueHistoryFiltersInfo from '@/features/IssueHistory/components/IssueHistoryFilters/IssueHistoryFiltersInfo';
import PageNavigationDropdown from '@/common/PageNavigationDropdown';
import QueryFilters from '@/components/QueryFilters';
import { deleteDispatchIssue } from '../../api/services/deleteDispatchIssue';
import { deleteIncidentIssueDocument } from '@/features/IncidentHistory/api/services/deleteIncidentIssueDocument';
import { downloadDispatchIssueWithCacheCsv } from '@/features/IssueHistory/api/services/downloadDispatchIssueCSV';
import useConfirmation from '@/hooks/useConfirmation';
import { useIssueHistory } from '@/features/IssueHistory';
import { useIssueHistoryDrawer } from '../IssueHistoryDrawer';
import usePopState from '@/hooks/usePopState';
import { ServiceIssueFiltersType, useServiceIssueFilters } from '@/hooks/useServiceIssueFilters';

export type IssueHistoryFiltersState = {
  startDatetime: string;
  endDatetime: string;
  airportCode: string[];
  tripServicerIataAirlineCodes: string[];
  taxiUsed: DispatchIssueWithCacheTaxiUsedEnum;
};
const initIssueHistoryFiltersState: ServiceIssueFiltersType = {
  startDatetime: TODAY,
  endDatetime: TODAY_EOD,
  airportCode: [],
  tripServicerIataAirlineCodes: [],
  taxiUsed: null,
};

const IssueHistoryFilters = (): JSX.Element => {
  const [{ loading, selected, issues, search }, setIssueHistoryState, { submitFilters }] = useIssueHistory(
    ({ loading, selected, issues, search }) => ({
      loading,
      selected,
      issues,
      search,
    })
  );
  // state
  const state = useServiceIssueFilters(({ state }) => state);
  const setState = useServiceIssueFilters(({ setState }) => setState);
  const saveState = useServiceIssueFilters(({ saveState }) => saveState);
  const rehydrateState = useServiceIssueFilters(({ rehydrate }) => rehydrate);

  const [lastQuery, setLastQuery] = useState(state);

  const setIssueHistoryDrawerState = useIssueHistoryDrawer(({ setState }) => setState);
  const confirmDelete = useConfirmation();

  const handleChange = useCallback(
    (name: string) =>
      (value: unknown): void =>
        setState((current) => ({ ...current, [name]: value })),
    [setState]
  );

  const handleSubmit = useCallback((): void => {
    console.log('IssueHistoryFilters: Submitting....');
    // get the latest state from closure to ensure it's always accurate
    const { state } = useServiceIssueFilters.getState();
    saveState();
    setLastQuery(state);
    submitFilters(state);
  }, [state, saveState]);

  const handleReset = useCallback((): void => {
    const startDatetime = new Datetime().startOf('day').toString();
    const endDatetime = new Datetime().endOf('day').toString();
    setState({ ...initIssueHistoryFiltersState, startDatetime, endDatetime });
    setLastQuery({ ...initIssueHistoryFiltersState, startDatetime, endDatetime });
    setIssueHistoryState((current) => ({ ...current, search: '' }));
    saveState();
  }, [setState, saveState]);

  const handlePopState = useCallback(() => rehydrateState().then(handleSubmit), [rehydrateState, handleSubmit]);
  usePopState(handlePopState);

  const onExportToCSV = useCallback(async () => {
    const { state } = useServiceIssueFilters.getState();
    const res = await downloadDispatchIssueWithCacheCsv([state]);
    if (!res) return;
    const blob = new Blob([res.data], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    saveFile(url, `DispatchIssueHistory-${new Datetime().format(DATE_INPUT_FORMAT)}.csv`);
  }, []);

  const onCreate = () => {
    console.log('Create Dispatch Issue');
    setIssueHistoryDrawerState((current) => ({ ...current, show: true }));
  };

  const onDelete = () =>
    confirmDelete()
      .then(async () => {
        try {
          // Get all of the documents and delete them first
          const documentIds = Array.from(selected.values())
            .map((issue) =>
              issue?.documents?.map((document) => ({
                id: document.id,
              }))
            )
            .flat();
          if (documentIds?.length > 0) {
            await deleteIncidentIssueDocument(documentIds);
          }
          const ids = Array.from(selected.keys()).map((id) => ({ id }));
          await deleteDispatchIssue(ids);
          setState((current) => ({
            ...current,
            selected: new Map(),
          }));
        } catch (err) {
          console.error('Error Deleting Incident.', err);
        } finally {
          return submitFilters?.(useServiceIssueFilters.getState().state);
        }
      })
      .catch(() => console.log('Cancelled delete'));
  const onOpenSettings = () => {
    console.log('Open Settings');
  };

  useEffect((): void => {
    // submit filters on page load without resetting state
    handleSubmit();
  }, []);

  return (
    <>
      <QueryFilters className="AccidentHistoryFilters justify-content-between">
        <div className="d-flex gap-2">
          <QueryFilters.Control>
            <DateRangePicker
              name="dates"
              isDirty={state.startDatetime !== lastQuery.startDatetime || state.endDatetime !== lastQuery.endDatetime}
              value={[state.startDatetime, state.endDatetime]}
              onChange={(event) => {
                const [from, to] = event.target.value?.split?.(' - ') || [null, null];
                handleChange('startDatetime')(from);
                handleChange('endDatetime')(to);
              }}
            />
          </QueryFilters.Control>
          <QueryFilters.Input>
            <AirportDropdown.Multi
              value={state.airportCode}
              onChange={handleChange('airportCode')}
              options={{ locale: { 'Select...': 'Airports' } }}
            />
          </QueryFilters.Input>
          <QueryFilters.Input>
            <AirlineIataDropdown.Multi value={state.tripServicerIataAirlineCodes} onChange={handleChange('tripServicerIataAirlineCodes')} />
          </QueryFilters.Input>
          <QueryFilters.Input>
            <EnumDropdown
              value={state.taxiUsed}
              onChange={handleChange('taxiUsed')}
              enum={DispatchIssueWithCacheTaxiUsedEnum}
              options={{ locale: { 'Select...': 'Taxi Called?' } }}
            />
          </QueryFilters.Input>
          <QueryFilters.Control>
            <Button className="border-white" name="SUBMIT" variant="success" onClick={handleSubmit} disabled={loading}>
              {loading && <i className="fa fa-spinner fa-pulse" />}
              {!loading && <span>Go</span>}
            </Button>
            <Button name="RESET" variant="outline-secondary-subtle" onClick={handleReset}>
              Reset
            </Button>
          </QueryFilters.Control>
          <QueryFilters.Control>
            <InputWithIcon
              icon={<i className="sv sv-magnifier fs-4" />}
              name="search"
              value={search}
              onChange={(value) => {
                setIssueHistoryState((current) => ({ ...current, search: value }));
              }}
              placeholder="Search"
            />
          </QueryFilters.Control>
        </div>
        <div className="d-flex justify-content-end gap-2">
          <QueryFilters.Control>
            <FormButton
              maxWidth={1560}
              icon={<i className="sv sv-plus-square {font-size:1.5rem;}" />}
              name="CREATE"
              variant="outline-secondary-subtle"
              onClick={onCreate}
            >
              Create Dispatch Issue
            </FormButton>
          </QueryFilters.Control>
          <QueryFilters.Input>
            <PageNavigationDropdown />
          </QueryFilters.Input>
          <QueryFilters.Control>
            <FormButton
              icon={<i className="sv sv-trash2 {font-size:1.5rem;}" />}
              name="DELETE"
              variant="outline-secondary-subtle"
              onClick={onDelete}
              disabled={selected.size === 0}
            />
          </QueryFilters.Control>
          <QueryFilters.Control>
            <FormButton
              icon={<i className="sv sv-excel {font-size:1.5rem;}" />}
              name="EXPORT_AS_CSV"
              variant="outline-secondary-subtle"
              onClick={onExportToCSV}
              feedback="Export to CSV"
              disabled={issues.size === 0}
            />
          </QueryFilters.Control>
          {/* <QueryFilters.Control>
            <FormButton
              variant="outline-secondary-subtle"
              onClick={onOpenSettings}
              icon={<i className="sv sv-cog {font-size:1.5rem;}" />}
              feedback="Dispatch Issue History Page Settings & Preferences"
            />
          </QueryFilters.Control> */}
        </div>
      </QueryFilters>
      <IssueHistoryFiltersInfo />
    </>
  );
};

export default IssueHistoryFilters;
